import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import moment from 'moment';

import { WAREHOUSE_BATCH_EXPIRY_DATE_SOURCE_NAME } from '../../../common/constants';
import { formatNumber } from '../../../common/utils/utils';
import { formatExpiryDateForDisplay } from '../../../common/utils/warehousing/formatExpiryBatchSummary';
import { sortWarehouseBatchesByPriority } from '../../../common/utils/warehousing/sortWarehouseBatchesByPriority';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Button, Dialog } from '../../components';
import Text from '../../components/text/Text';

import { normalizeBatchRows } from './StockDeliveryDetailHelpers';

const DeliveryBatchesPreviewDialog = ({
	item,
	timezone,
	onClose,
}) => {
	const styles = useThemedStyles(createStyles);
	const batches = useMemo(() => {
		return sortWarehouseBatchesByPriority(normalizeBatchRows(item?.expiryBatches || [], timezone))
			.filter(batch => batch.quantity > 0);
	}, [item?.expiryBatches, timezone]);
	const totalQuantity = useMemo(() => batches.reduce((acc, batch) => acc + batch.quantity, 0), [batches]);

	return (
		<Dialog
			isOpen={Boolean(item)}
			onClose={onClose}
			shouldShowCloseIcon={true}
			enableCloseDialog={true}
		>
			{item && (
				<View style={styles.previewDialogContainer}>
					<Text style={styles.dialogTitle}>
						Batches in warehouse
					</Text>
					<Text style={styles.dialogMeta}>
						Product: {item.product?.name || 'Unknown product'}
					</Text>
					<Text style={styles.dialogMeta}>
						Total quantity in listed batches: {formatNumber(totalQuantity)} units
					</Text>
					<Text style={styles.dialogMeta}>
						Rows are shown in serving order.
					</Text>
					<ScrollView
						style={styles.previewScroll}
						contentContainerStyle={styles.previewScrollContent}
					>
						{!batches.length && (
							<Text style={styles.dialogMeta}>No batches captured.</Text>
						)}
						{batches.map((batch, index) => (
							<View
								key={`${item.id}_${index}`}
								style={styles.previewRow}
							>
								<Text style={styles.previewRowText}>
									#{index + 1} {formatExpiryDateForDisplay(batch.expiryDate) || 'Unknown'}: {batch.quantity} units
								</Text>
								<Text style={styles.previewRowCode}>
									Received: {batch.receivedAt ? moment(batch.receivedAt).format('DD MMM YYYY') : 'Unknown'}
								</Text>
								<Text style={styles.previewRowCode}>
									Source: {WAREHOUSE_BATCH_EXPIRY_DATE_SOURCE_NAME[batch.expiryDateSource] || 'Unknown'}
								</Text>
								{Boolean(batch.supplierBatchCode) && (
									<Text style={styles.previewRowCode}>
										Code: {batch.supplierBatchCode}
									</Text>
								)}
							</View>
						))}
					</ScrollView>
					<View style={styles.previewDialogFooter}>
						<Button
							label="Close"
							onPress={onClose}
						/>
					</View>
				</View>
			)}
		</Dialog>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	previewDialogContainer: {
		width: '100%',
		maxHeight: '80%',
	},
	previewScroll: {
		marginTop: space.sm,
		maxHeight: 340,
		borderWidth: 1,
		borderColor: colors.border,
		borderRadius: radius.card,
	},
	previewScrollContent: {
		paddingHorizontal: space.md,
		paddingVertical: space.xs,
	},
	previewRow: {
		paddingVertical: space.sm,
		borderBottomWidth: 1,
		borderBottomColor: colors.border,
		gap: space.xxs,
	},
	previewRowText: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	previewRowCode: {
		...type.secondary,
		color: colors.textSecondary,
	},
	previewDialogFooter: {
		marginTop: space.md,
		alignItems: 'flex-end',
	},
	dialogTitle: {
		...type.cardTitle,
		color: colors.text,
		marginBottom: space.sm,
		paddingRight: space.xxl,
	},
	dialogMeta: {
		...type.bodySmall,
		color: colors.textSecondary,
		marginBottom: space.sm,
	},
});

export default DeliveryBatchesPreviewDialog;
